import { useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Play, Upload, Shield, Sparkles, Zap, Globe,
  ArrowRight, Check, Star, Film, HardDrive, Lock,
} from 'lucide-react'
import ParticleBackground from '../components/ParticleBackground'
import { trackEvent } from '../lib/tracking'

export default function Home() {
  useEffect(() => {
    trackEvent('page_view', { page: 'home' })
  }, [])

  const handleSignIn = () => {
    trackEvent('signin_click', { source: 'home_hero' })
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
    const params = new URLSearchParams({
      return_to: '/',
      session_id: localStorage.getItem('cs_session_id') || '',
      visitor_id: localStorage.getItem('cs_visitor_id') || '',
    })
    window.location.href = `${apiUrl}/api/v1/auth/google/start?${params.toString()}`
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-void-black">
      <ParticleBackground />
      <div className="absolute inset-0 bg-gradient-nebula opacity-30" />

      {/* Top Nav */}
      <nav className="relative z-20 border-b border-white/5 backdrop-blur-xl bg-void-black/60">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-cosmic">
              <HardDrive className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold gradient-text">Cloudsize</span>
          </div>

          <div className="hidden items-center gap-8 md:flex">
            <a href="#features" className="text-sm text-star-white/70 hover:text-star-white transition">Features</a>
            <a href="#pricing" className="text-sm text-star-white/70 hover:text-star-white transition">Pricing</a>
            <a href="#showcase" className="text-sm text-star-white/70 hover:text-star-white transition">Showcase</a>
            <a href="/nepali_compiled_videos" className="text-sm text-star-white/70 hover:text-star-white transition">Explore</a>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleSignIn}
              className="text-sm text-star-white/70 hover:text-star-white transition"
            >
              Sign In
            </button>
            <button
              onClick={() => trackEvent('get_started_click', { source: 'nav' })}
              className="glass-button rounded-xl px-4 py-2 text-sm font-semibold"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 px-6 py-20 md:py-32">
        <div className="mx-auto max-w-5xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-nebula-purple/30 bg-nebula-purple/10 px-4 py-1.5 text-sm text-nebula-purple backdrop-blur"
          >
            <Sparkles className="h-3.5 w-3.5" />
            <span>Now with AI-powered video insights</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="mb-6 text-5xl font-bold leading-[1.1] md:text-7xl lg:text-8xl"
          >
            <span className="gradient-text">Your videos.</span>
            <br />
            <span className="text-star-white">Infinite space.</span>
            <br />
            <span className="bg-gradient-to-r from-aurora-cyan to-nebula-purple bg-clip-text text-transparent">
              Cinematic experience.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mx-auto mb-10 max-w-2xl text-lg text-star-white/70 md:text-xl"
          >
            The cloud storage built for creators. Stream, share, and manage your videos
            with a platform that feels like the future — not a filing cabinet.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="flex flex-col items-center justify-center gap-3 sm:flex-row"
          >
            <button
              onClick={handleSignIn}
              className="group flex items-center justify-center gap-3 rounded-xl bg-gradient-cosmic px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-nebula-purple/30 transition-all hover:shadow-nebula-purple/60 hover:scale-[1.03]"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Start Free with Google
              <ArrowRight className="h-5 w-5 transition group-hover:translate-x-1" />
            </button>

            <a
              href="#demo"
              onClick={() => trackEvent('watch_demo_click')}
              className="glass-button flex items-center justify-center gap-3 rounded-xl px-8 py-4 text-lg font-semibold"
            >
              <Play className="h-5 w-5" />
              Watch Demo
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-6 text-xs text-star-white/50"
          >
            <div className="flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5 text-green-400" />
              <span>Free 100GB</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5 text-green-400" />
              <span>No credit card</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Shield className="h-3.5 w-3.5 text-cosmic-blue" />
              <span>DMCA compliant</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5 text-aurora-cyan" />
              <span>End-to-end encrypted</span>
            </div>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mx-auto mt-20 grid max-w-4xl grid-cols-2 gap-6 md:grid-cols-4"
        >
          <StatCard value="2.4M+" label="Videos hosted" />
          <StatCard value="48PB" label="Data streamed" />
          <StatCard value="180+" label="Countries" />
          <StatCard value="99.99%" label="Uptime" />
        </motion.div>
      </section>

      {/* Features */}
      <section id="features" className="relative z-10 px-6 py-24">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-4xl font-bold md:text-5xl">
              <span className="gradient-text">Built for creators,</span>
              <br />
              <span className="text-star-white">not files</span>
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-star-white/60">
              Every feature designed with video-first creators in mind. No more fighting with generic file managers.
            </p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <FeatureCard
              icon={Upload}
              color="from-pink-500 to-rose-500"
              title="Chunked Upload"
              desc="Resumable uploads with live thumbnail generation. Never lose progress on large files."
              delay={0.0}
            />
            <FeatureCard
              icon={Film}
              color="from-purple-500 to-fuchsia-500"
              title="Cinematic Player"
              desc="HLS adaptive streaming with AI-generated chapters, waveforms, and auto-subtitles."
              delay={0.1}
            />
            <FeatureCard
              icon={Sparkles}
              color="from-cyan-500 to-blue-500"
              title="AI Insights"
              desc="Scene detection, object recognition, speech-to-text, and smart search across your library."
              delay={0.2}
            />
            <FeatureCard
              icon={Zap}
              color="from-yellow-500 to-amber-500"
              title="Live Hover Preview"
              desc="Scrub through any video in your library by hovering. Waveform overlay included."
              delay={0.3}
            />
            <FeatureCard
              icon={Globe}
              color="from-emerald-500 to-teal-500"
              title="Global CDN"
              desc="Deliver videos at the edge. Sub-50ms first-frame latency from 180+ countries."
              delay={0.4}
            />
            <FeatureCard
              icon={Shield}
              color="from-indigo-500 to-purple-600"
              title="DMCA Tracking"
              desc="Every view is logged with IP, geolocation, and device data. Copyright protection built in."
              delay={0.5}
            />
          </div>
        </div>
      </section>

      {/* Showcase */}
      <section id="showcase" className="relative z-10 px-6 py-24">
        <div className="mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-nebula-purple/10 via-cosmic-blue/5 to-aurora-cyan/5 p-10 md:p-16"
          >
            <div className="grid items-center gap-10 md:grid-cols-2">
              <div>
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1 text-xs text-star-white/70">
                  <Star className="h-3 w-3 text-yellow-400" />
                  <span>Featured Drive</span>
                </div>
                <h3 className="mb-4 text-3xl font-bold text-star-white md:text-4xl">
                  Explore Public Drives
                </h3>
                <p className="mb-6 text-star-white/70">
                  Discover compilations, creator archives, and viral collections shared by our community.
                  Every drive is DMCA-tracked and sign-in protected.
                </p>
                <a
                  href="/nepali_compiled_videos"
                  onClick={() => trackEvent('explore_drive_click', { drive: 'nepali_compiled_videos' })}
                  className="group inline-flex items-center gap-2 rounded-xl bg-gradient-cosmic px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-nebula-purple/30 hover:scale-105 transition"
                >
                  Explore Nepali Archive
                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                </a>
              </div>
              <div className="relative">
                <div className="grid grid-cols-2 gap-3">
                  <div className="aspect-video rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 shadow-lg" />
                  <div className="aspect-video rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-500 shadow-lg mt-6" />
                  <div className="aspect-video rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 shadow-lg" />
                  <div className="aspect-video rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 shadow-lg mt-6" />
                </div>
                <div className="pointer-events-none absolute -inset-4 -z-10 rounded-3xl bg-gradient-cosmic opacity-20 blur-3xl" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="relative z-10 px-6 py-24">
        <div className="mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12 text-center"
          >
            <h2 className="mb-4 text-4xl font-bold gradient-text md:text-5xl">
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-star-white/60">Start free. Scale when you're ready.</p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-3">
            <PriceCard
              name="Free"
              price="$0"
              period="forever"
              features={['100 GB storage', 'HD streaming', 'Basic AI insights', '7-day trash recovery']}
              cta="Get Started"
              onClick={() => trackEvent('pricing_select', { plan: 'free' })}
            />
            <PriceCard
              name="Creator"
              price="$12"
              period="/month"
              featured
              features={['2 TB storage', '4K streaming', 'Full AI suite', 'Custom share links', 'Priority support']}
              cta="Start Creator"
              onClick={() => trackEvent('pricing_select', { plan: 'creator' })}
            />
            <PriceCard
              name="Studio"
              price="$49"
              period="/month"
              features={['Unlimited storage', '8K streaming', 'Team collaboration', 'API access', 'White-label option']}
              cta="Contact Sales"
              onClick={() => trackEvent('pricing_select', { plan: 'studio' })}
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 px-6 py-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="mx-auto max-w-4xl rounded-3xl border border-nebula-purple/30 bg-gradient-to-br from-nebula-purple/20 via-cosmic-blue/10 to-aurora-cyan/10 p-12 text-center backdrop-blur-xl md:p-16"
        >
          <h2 className="mb-4 text-4xl font-bold text-star-white md:text-5xl">
            Ready to upgrade your video workflow?
          </h2>
          <p className="mx-auto mb-8 max-w-xl text-lg text-star-white/70">
            Join 2.4M+ creators who've moved from boring file lists to a cinematic cloud.
          </p>
          <button
            onClick={() => trackEvent('final_cta_click')}
            className="group inline-flex items-center gap-3 rounded-xl bg-white px-8 py-4 text-lg font-bold text-void-black shadow-2xl hover:scale-105 transition"
          >
            Start free — no credit card
            <ArrowRight className="h-5 w-5 transition group-hover:translate-x-1" />
          </button>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 px-6 py-10">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-cosmic">
                <HardDrive className="h-4 w-4 text-white" />
              </div>
              <span className="font-bold text-star-white">Cloudsize</span>
              <span className="text-xs text-star-white/40">© 2026</span>
            </div>
            <div className="flex gap-6 text-sm text-star-white/50">
              <a href="#" className="hover:text-star-white transition">Privacy</a>
              <a href="#" className="hover:text-star-white transition">Terms</a>
              <a href="#" className="hover:text-star-white transition">DMCA</a>
              <a href="#" className="hover:text-star-white transition">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

function StatCard({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-5 text-center backdrop-blur">
      <div className="mb-1 text-3xl font-bold gradient-text md:text-4xl">{value}</div>
      <div className="text-xs text-star-white/50 md:text-sm">{label}</div>
    </div>
  )
}

function FeatureCard({
  icon: Icon,
  color,
  title,
  desc,
  delay,
}: {
  icon: any
  color: string
  title: string
  desc: string
  delay: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.5 }}
      className="group relative overflow-hidden rounded-2xl border border-white/5 bg-white/[0.02] p-7 backdrop-blur-sm transition-all hover:border-white/20 hover:bg-white/[0.04] hover:-translate-y-1"
    >
      <div className={`mb-5 inline-flex rounded-xl bg-gradient-to-br ${color} p-3 shadow-lg`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      <h3 className="mb-2 text-xl font-bold text-star-white">{title}</h3>
      <p className="text-sm text-star-white/60 leading-relaxed">{desc}</p>
      <div className={`pointer-events-none absolute -bottom-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br ${color} opacity-0 blur-3xl transition-opacity group-hover:opacity-20`} />
    </motion.div>
  )
}

function PriceCard({
  name,
  price,
  period,
  features,
  cta,
  featured,
  onClick,
}: {
  name: string
  price: string
  period: string
  features: string[]
  cta: string
  featured?: boolean
  onClick: () => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`relative rounded-2xl border p-8 backdrop-blur-sm ${
        featured
          ? 'border-nebula-purple/50 bg-gradient-to-br from-nebula-purple/20 to-cosmic-blue/10 shadow-2xl shadow-nebula-purple/20 scale-[1.03]'
          : 'border-white/5 bg-white/[0.02]'
      }`}
    >
      {featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-cosmic px-3 py-1 text-xs font-bold text-white">
          MOST POPULAR
        </div>
      )}
      <h3 className="mb-2 text-xl font-bold text-star-white">{name}</h3>
      <div className="mb-6">
        <span className="text-4xl font-bold text-star-white">{price}</span>
        <span className="text-sm text-star-white/50">{period}</span>
      </div>
      <ul className="mb-8 space-y-3">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-star-white/80">
            <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-nebula-purple" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <button
        onClick={onClick}
        className={`w-full rounded-xl px-6 py-3 font-semibold transition ${
          featured
            ? 'bg-gradient-cosmic text-white shadow-lg shadow-nebula-purple/30 hover:scale-[1.02]'
            : 'border border-white/10 bg-white/5 text-star-white hover:bg-white/10'
        }`}
      >
        {cta}
      </button>
    </motion.div>
  )
}
